/* */
import { removeNullOfObject } from 'helpers';
import Queue from 'helpers/queue';
import { findNode, removeNode } from 'helpers/responder';
import produce from 'immer';
import { cloneDeep, isArray } from 'lodash';

const deleteNestedChildrenNode = (list) => {
  if (isArray(list)) {
    list.forEach((node) => {
      node.deleted = true;

      if (isArray(node?.children)) {
        deleteNestedChildrenNode(node?.children);
      }
    });
  }
};

//! Delete content responder
export const baseDeleteContent = () => {
  return {
    isDeletingDialog: false,
    errorDeletingDialog: null,
  };
};

export const fetchDeleteContent = (actions, state) => {
  return produce(state, (draft) => {
    draft.isDeletingDialog = true;
  });
};

export const fetchDeleteContentSuccess = (actions, state) => {
  return produce(state, (draft) => {
    const { softDelete } = actions;
    const nodeId = actions.id;

    if (softDelete) {
      const thisNode = findNode()(nodeId, draft.dialogDetail.children);
      thisNode.deleted = true;

      deleteNestedChildrenNode(thisNode?.children);

      draft.dialogDetail.children = cloneDeep(draft.dialogDetail.children);
      draft.isDeletingDialog = false;
      draft.errorDeletingDialog = null;
      draft.selectedNode = {};
      return;
    }

    const listAfterRemove = cloneDeep(removeNode()(nodeId, draft.dialogDetail.children));

    draft.dialogDetail.children = listAfterRemove;
    draft.isDeletingDialog = false;
    draft.errorDeletingDialog = null;
    draft.selectedNode = {};
  });
};

export const fetchDeleteContentFailed = (actions, state) => {
  return produce(state, (draft) => {
    draft.isDeletingDialog = false;
    draft.errorDeletingDialog = actions.error;
  });
};

//! Save content responder
export const fetchSaveContent = (actions, state) => {
  return produce(state, (draft) => {
    draft.isSavingDialog = true;
  });
};

export const fetchSaveContentSuccess = (actions, state) => {
  return produce(state, (draft) => {
    draft.isSavingDialog = false;
    const tempDialogDetailChild = cloneDeep(draft.dialogDetail.children);

    const isAddedNode = !!actions.newNodeAdded;
    const isUpdatedNode = !!actions.selectNodeUpdated;
    const isClonedNode = !!actions.newNodeClone;

    if (isClonedNode) {
      const newNodeClone = actions.newNodeClone;
      const nodeParent = findNode()(newNodeClone.parentId, tempDialogDetailChild);

      //* If clone node from other node
      if (!!nodeParent?.children) {
        if (isArray(nodeParent?.children)) {
          nodeParent.children = [...nodeParent.children, newNodeClone];
        } else {
          // If children of node is null/undefine
          nodeParent.children = [newNodeClone];
        }
      } else {
        //* If clone node from Root
        tempDialogDetailChild.push(newNodeClone);
      }

      draft.dialogDetail.children = tempDialogDetailChild;
      draft.selectedNode = {};
    }

    if (isUpdatedNode) {
      const selectNodeUpdated =
        (typeof actions.selectNodeUpdated === 'object' && actions.selectNodeUpdated) || {};
      if (Object.keys(selectNodeUpdated).length !== 0) {
        //* HANDLE MUTABLE VARIABLE
        //* Update node in list
        //* Find new parentId of node just update
        const nodeParent = findNode()(selectNodeUpdated.parentId, tempDialogDetailChild);

        //* Case 1: Parent include node
        if (nodeParent?.children?.map((el) => el.id)?.includes(selectNodeUpdated.id)) {
          const thisNode = findNode()(selectNodeUpdated.id, tempDialogDetailChild);
          const noteUpdateNotNull = removeNullOfObject(selectNodeUpdated, [
            'deliveredCgId',
            'replierCgId',
            'clickerCgId',
            'convertorCgId',
            'listenerCgId',
            'callbackCgId',
            'ivrAnswerCgId',
            'ivrClickThroughCgId',
          ]);
          Object.assign(thisNode, { ...noteUpdateNotNull });
          draft.dialogDetail.children = tempDialogDetailChild;
        } else {
          //* Case 2: Move node from one parent to other parent
          //* Remove node
          const oldNode = findNode()(selectNodeUpdated.id, tempDialogDetailChild);
          const newChidlren = removeNode()(selectNodeUpdated.id, tempDialogDetailChild);
          const nodeParent = findNode()(selectNodeUpdated.parentId, newChidlren);

          const noteUpdateNotNull = removeNullOfObject(selectNodeUpdated, [
            'deliveredCgId',
            'replierCgId',
            'clickerCgId',
            'convertorCgId',
            'listenerCgId',
            'callbackCgId',
            'ivrAnswerCgId',
            'ivrClickThroughCgId',
          ]);
          Object.assign(oldNode, { ...noteUpdateNotNull });

          if (nodeParent) {
            if (nodeParent.children) {
              nodeParent.children.push(oldNode);
              nodeParent.children.sort((prevNode, curNode) => prevNode.id - curNode.id);
            } else {
              nodeParent.children = [oldNode];
            }
          } else {
            //* If nodeParent is undefined -> is Root Node
            newChidlren.push(oldNode);
          }

          //* Re-arrange when connected
          newChidlren.sort((prevNode, curNode) => prevNode.id - curNode.id);
          draft.dialogDetail.children = newChidlren;
        }

        // update selected node
        draft.selectedNode = {
          ...draft.selectedNode,
          ...selectNodeUpdated,
          isNew: false,
        };
      }
    }

    if (isAddedNode) {
      // For create new node
      const { parentId, dialogId } = actions.newNodeAdded || {};

      // Check if create from root node
      if (parentId === dialogId) {
        if (draft.dialogDetail.children) {
          draft.dialogDetail.children.push(actions.newNodeAdded);
        } else {
          // If children of node is null/undefine
          draft.dialogDetail.children = [actions.newNodeAdded];
        }
      }
      // Check if create from child node
      else {
        const thisNode = findNode()(parentId, tempDialogDetailChild);
        if (thisNode.children && typeof thisNode.children === 'object') {
          thisNode.children = [...thisNode.children, actions.newNodeAdded];
        } else {
          // If children of node is null/undefine
          thisNode.children = [actions.newNodeAdded];
        }

        draft.dialogDetail.children = tempDialogDetailChild;
      }

      draft.selectedNode = {};
    }
  });
};

export const fetchSaveContentFailed = (actions, state) => {
  return produce(state, (draft) => {
    draft.isSavingDialog = false;
    draft.errorSaving = actions.error;
  });
};

//! fetch responder template
export const baseResponderTemplate = () => {
  return {
    isFetchingDialogTemplate: false,
    listDialogTemplate: [],
    errorDialogTemplate: null,
  };
};

export const fetchListResponderTemplate = (actions, state) => {
  return produce(state, (draft) => {
    draft.isFetchingDialogTemplate = true;
  });
};

export const fetchListResponderTemplateSuccess = (actions, state) => {
  const { dataTemplate, isLast } = actions;
  return produce(state, (draft) => {
    draft.isFetchingDialogTemplate = false;
    draft.errorDialogTemplate = null;
    draft.listDialogTemplate = dataTemplate || draft.listDialogTemplate;
    draft.isLastResponder = isLast;
  });
};

export const fetchListResponderTemplateFailed = (actions, state) => {
  const { error } = actions;
  return produce(state, (draft) => {
    draft.isFetchingDialogTemplate = false;
    draft.errorDialogTemplate = error;
  });
};

//! fetch responder
export const baseResponder = () => {
  return {
    isFetchingDialog: false,
    listDialog: [],
    errorDialog: null,
  };
};

export const fetchListResponder = (actions, state) => {
  return produce(state, (draft) => {
    draft.isFetchingDialog = true;
  });
};

export const fetchListResponderSuccess = (actions, state) => {
  const { data, isLast } = actions;
  return produce(state, (draft) => {
    draft.isFetchingDialog = false;
    draft.listDialog = data || draft.listDialog;
    draft.isLastResponder = isLast;
  });
};

export const fetchListResponderFailed = (actions, state) => {
  const { error } = actions;
  return produce(state, (draft) => {
    draft.isFetchingDialog = false;
    draft.errorDialog = error;
  });
};

// Switch status
const queue = new Queue();
export const baseSwitchStatus = () => {
  return { queueResponder: [] };
};

export const switchStatus = (actions, state) => {
  queue.pushToQueue(actions.id);
  return produce(state, (draft) => {
    draft.queueResponder = queue.queue;
  });
};

export const switchStatusSuccess = (actions, state) => {
  queue.removeFromQueue(actions.id);
  return produce(state, (draft) => {
    draft.queueResponder = queue.queue;
  });
};

export const switchStatusFailed = (actions, state) => {
  queue.removeFromQueue(actions.id);
  return produce(state, (draft) => {
    draft.queueResponder = queue.queue;
  });
};

// Filter responder
export const refetchResponderSuccess = (actions, state) => {
  return produce(state, (draft) => {
    draft.listDialog = actions.data;
  });
};

export const refetchResponderFailed = (actions, state) => {
  return produce(state, (draft) => {});
};

export const baseFilterResponder = () => {
  return {
    isFiltering: false,
    errorFiltering: null,
  };
};

export const fetchFilterResponder = (actions, state) => {
  return produce(state, (draft) => {
    draft.isFiltering = true;
  });
};

export const fetchFilterResponderSuccess = (actions, state) => {
  const isResponderTemplate = !actions.published;
  return produce(state, (draft) => {
    draft.isFiltering = false;
    draft.errorFiltering = null;
    draft.isLastResponder = actions.isLast;

    if (isResponderTemplate) {
      draft.listDialogTemplate = actions.data || draft.listDialogTemplate;
    } else {
      draft.listDialog = actions.data || draft.listDialog;
    }
  });
};

export const fetchFilterResponderFailed = (actions, state) => {
  return produce(state, (draft) => {
    draft.isFiltering = false;
    draft.errorFiltering = actions.error;
  });
};

// Set load more
export const baseLoadMore = () => {
  return {
    isLoadingMore: false,
    errorLoadMore: null,
    isLastResponder: false,
  };
};

export const fetchLoadMore = (actions, state) => {
  return produce(state, (draft) => {
    draft.isLoadingMore = true;
  });
};

export const fetchLoadMoreSuccess = (actions, state) => {
  const isResponderTemplate = !actions.published;
  return produce(state, (draft) => {
    draft.isLoadingMore = false;
    draft.isLastResponder = actions.isLast;
    if (isResponderTemplate) {
      draft.listDialogTemplate = [...draft.listDialogTemplate, ...actions.data];
    } else {
      draft.listDialog = [...draft.listDialog, ...actions.data];
    }
  });
};

export const fetchLoadMoreFailed = (actions, state) => {
  return produce(state, (draft) => {
    draft.isLoadingMore = false;
    draft.errorLoadMore = actions.error;
  });
};

// Set did group in responder
export const baseSetDid = () => {
  return {
    isSettingDid: false,
  };
};

export const fetchSetDID = (actions, state) => {
  return produce(state, (draft) => {
    draft.isSettingDid = true;
  });
};

export const fetchSetDIDSuccess = (actions, state) => {
  return produce(state, (draft) => {
    draft.dialogDetail.didGroupId = actions.data.didGroupId;
    draft.dialogDetail.didGroupName = actions.data.didGroupName;
    draft.isSettingDid = false;
  });
};

export const fetchSetDIDFailed = (actions, state) => {
  return produce(state, (draft) => {
    draft.isSettingDid = false;
  });
};
